import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ApAdvanceRequest,
  ApAdvanceRequestInsert,
  ApAdvanceRequestSearchCommand,
  ApAdvanceRequestUpdate,
} from '../models/ap-advance-request/ap-advance-request';
import { ApAdvancePayment } from '../models/ap-advance/ap-advance';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class ApAdvanceRequestProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/ap/advance-requests';

  public getById(id: string): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.get<ApiResponse<ApAdvanceRequest>>(`${this.baseUrl}/${id}`);
  }

  public createDraft(data: ApAdvanceRequestInsert): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.post<ApiResponse<ApAdvanceRequest>>(this.baseUrl, data);
  }

  public update(id: string, data: ApAdvanceRequestUpdate): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.put<ApiResponse<ApAdvanceRequest>>(`${this.baseUrl}/${id}`, data);
  }

  public submit(id: string): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.post<ApiResponse<ApAdvanceRequest>>(`${this.baseUrl}/${id}/submit`, {});
  }

  public approve(id: string): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.post<ApiResponse<ApAdvanceRequest>>(`${this.baseUrl}/${id}/approve`, {});
  }

  public reject(id: string): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.post<ApiResponse<ApAdvanceRequest>>(`${this.baseUrl}/${id}/reject`, {});
  }

  public cancel(id: string): Observable<ApiResponse<ApAdvanceRequest>> {
    return this.httpClient.post<ApiResponse<ApAdvanceRequest>>(`${this.baseUrl}/${id}/cancel`, {});
  }

  /** Opens (or spawns) the 1:1 draft advance payment for an approved request. Idempotent. */
  public createOrGetAdvance(id: string): Observable<ApiResponse<ApAdvancePayment>> {
    return this.httpClient.post<ApiResponse<ApAdvancePayment>>(`${this.baseUrl}/${id}/advance`, {});
  }

  public search(
    command: ApAdvanceRequestSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<ApAdvanceRequest>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<ApAdvanceRequest>>>(`${this.baseUrl}/search`, command, {
      params,
    });
  }

  /**
   * Deletes a DRAFT document (#5223). The API refuses anything further along, so this is safe to offer
   * from a draft screen and safe to have refused if the status moved on in another tab.
   */
  public delete(id: string): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/${id}`);
  }
}
